/**
 * ui.js —— 工具站原生组件运行时
 * ---------------------------------------------------------------------------
 * 定位：把国内工具站最高频的几种交互封装成可复用组件，纯原生 JS、零依赖、
 *      零网络请求。它只负责「交互」与「界面状态」，不含任何计算逻辑，
 *      也不含任何业务文案（文案统一从 window.ToolI18n 读取）。
 *
 * 提供的组件：
 *   ToolUI.toast()       轻提示（顶部居中，Ant Design Message 形态）
 *   ToolUI.copy()        一键复制（带 Clipboard API 降级 + 按钮态反馈）
 *   ToolUI.segmented()   分段控制器 / 选项卡
 *   ToolUI.theme         亮暗主题切换（记忆 + 跟随系统）
 *   ToolUI.backTop       回到顶部悬浮按钮
 *   ToolUI.homeFilter()  首页搜索 + 分类筛选（纯前端，不改动 DOM 结构）
 *   ToolUI.recent()      计算历史（localStorage 记忆，可清空）
 *   ToolUI.favs()        我的收藏（localStorage，工具页收藏 + 首页聚合行）
 *   ToolUI.visits()      最近使用（localStorage，全站级足迹，可单删/清空）
 *   ToolUI.ready()       DOM 就绪回调
 *
 * 声明式用法（在 HTML 上加 data 属性即可自动生效）：
 *   <button data-tool-ui="theme">…</button>
 *   <button data-tool-ui="backTop">…</button>
 *   <div data-tool-ui="homeFilter">…</div>
 *
 * 挂载到 window.ToolUI，与 window.ToolEngine（计算）、window.ToolI18n（文案）
 * 三者职责互不重叠。
 */
(function (global) {
  'use strict';

  var doc = global.document;

  /* ------------------------------------------------------------------ *
   * 工具函数
   * ------------------------------------------------------------------ */

  /** 读取当前语言包（由 head 中的内联脚本写入 window.TOOLS_LANG） */
  function dict() {
    var packs = global.ToolI18n || {};
    // 语言包按当前站点语言取；缺该语言包时回退「站点组的基准语言」
    // （layout/header.html 注入：中文站 = zh-CN，海外站 = en）。
    // 这里刻意不硬编码回退 zh-CN —— 否则海外站在语言包缺失时会整页冒中文。
    var lang = global.TOOLS_LANG || '';
    var fallback = global.TOOLS_FALLBACK_LANG || 'zh-CN';
    return packs[lang] || packs[fallback] || {};
  }

  /**
   * 结果文案里的标点（2026-09-18）
   *
   * 为什么单列一套：partial 把拼进结果的标点硬编码成全角（`：（）　、；·`）——
   * 中文站是对的，英文站会露出「Uppercase：ABC」「5 (max）」这类混搭（68 处 / 28 个工具，
   * `node scripts/audit-inline-cjk.mjs` 可复现）。
   *
   * **刻意不走 t() 的兜底链**：t() 缺键会回退到传入的默认值，而默认值一定是中文标点 ——
   * 那等于把泄漏原样搬回来。这里缺键返回空串：宁可少一个标点，也不能在英文站吐全角。
   */
  function punct(key) {
    var p = (dict().ui || {}).punct;
    var v = p ? p[key] : '';
    return v == null ? '' : String(v);
  }

  /** 按 "a.b.c" 取文案，缺失时回退到 fallback */
  function t(key, fallback) {
    var cur = dict();
    var parts = String(key).split('.');
    for (var i = 0; i < parts.length; i++) {
      if (cur == null || typeof cur !== 'object') return fallback == null ? '' : fallback;
      cur = cur[parts[i]];
    }
    if (cur == null || cur === '') return fallback == null ? '' : fallback;
    return cur;
  }

  /** DOM 就绪 */
  function ready(fn) {
    if (!doc) return;
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /**
   * HTML 侧文案的声明式填充（partial 里不写死中文，海外站才不会冒中文）：
   *   [data-i18n-aria]  可访问性名称
   *   [data-i18n-title] title 属性（如 iframe 预览区）
   *   [data-i18n-ph]    placeholder
   *   [data-i18n-fill]  输入框默认值（仅在元素为空时填，不覆盖工具自身的默认值逻辑）
   *   [data-i18n-text]  元素文字（如 select 的 option）
   * 取值：当前站点语言包的工具段 → 全局 ui 段（dict() 内含「站点组基准语言」回退），
   * 都缺则留空 —— 与 feedback.js / 工具运行器同一条口径。
   * slug 从路径末段取（工具页 URL 为 /tools[/en]/{slug}），partial 无需额外声明。
   */
  function fillDeclaredCopy() {
    var nodes = qa('[data-i18n-aria],[data-i18n-title],[data-i18n-ph],[data-i18n-fill],[data-i18n-text]');
    if (!nodes.length) return;
    var filled = false;
    var slug = String((global.location && global.location.pathname) || '').split('/').filter(Boolean).pop() || '';
    slug = slug.replace(/\.html$/, '');
    function pick(key) {
      var v = slug ? t(slug + '.' + key, '') : '';
      if (typeof v !== 'string' || v === '') v = t('ui.' + key, '');
      return (typeof v === 'string') ? v : '';
    }
    nodes.forEach(function (el) {
      var kAria = el.getAttribute('data-i18n-aria');
      if (kAria) el.setAttribute('aria-label', pick(kAria));
      var kTitle = el.getAttribute('data-i18n-title');
      if (kTitle) el.setAttribute('title', pick(kTitle));
      var kPh = el.getAttribute('data-i18n-ph');
      if (kPh) el.setAttribute('placeholder', pick(kPh));
      var kFill = el.getAttribute('data-i18n-fill');
      if (kFill) {
        // 只在元素为空时填（不覆盖工具自身的默认值逻辑）；填完通知一次 input ——
        // 默认值从 HTML 内容迁到语言包后，工具自己的 IIFE 早于本填充执行，
        // 首屏会读到空值而不计算，这里补一次「用户输入了默认值」的信号。
        var fv = pick(kFill);
        if (fv && !el.value) {
          el.value = fv;
          filled = true;
          if ('defaultValue' in el) { el.defaultValue = fv; }
          el.dispatchEvent(new global.Event('input', { bubbles: true }));
        }
      }
      var kText = el.getAttribute('data-i18n-text');
      if (kText) el.textContent = pick(kText);
    });
    // 声明式默认值就位后广播一次：ToolRunner 据此重拍默认值快照，否则「重置」会恢复成空。
    if (filled && doc.dispatchEvent) {
      doc.dispatchEvent(new global.Event('tool:declared-fill'));
    }
  }

  /** querySelector 简写（作用域可选） */
  function q(sel, scope) { return (scope || doc).querySelector(sel); }
  function qa(sel, scope) {
    return Array.prototype.slice.call((scope || doc).querySelectorAll(sel));
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /** 从 SVG sprite 取一个图标 */
  function icon(name, cls) {
    return '<svg class="tool-icon ' + (cls || '') + '" aria-hidden="true"><use href="#i-' + name + '"></use></svg>';
  }

  /* ------------------------------------------------------------------ *
   * 组件一：轻提示 Toast
   * ------------------------------------------------------------------ */
  var toastHost = null;

  function ensureHost() {
    if (toastHost && doc.body.contains(toastHost)) return toastHost;
    toastHost = doc.createElement('div');
    toastHost.className = 'tool-toast-host';
    toastHost.setAttribute('role', 'status');
    toastHost.setAttribute('aria-live', 'polite');
    doc.body.appendChild(toastHost);
    return toastHost;
  }

  /**
   * 顶部轻提示
   * @param {string} msg   提示文案
   * @param {string} type  ok | err | info
   * @param {number} ms    停留毫秒
   */
  function toast(msg, type, ms) {
    if (!doc || !doc.body || !msg) return;
    type = type || 'info';
    var host = ensureHost();
    var el = doc.createElement('div');
    el.className = 'tool-toast tool-toast--' + type;
    var iconName = type === 'ok' ? 'check-circle' : (type === 'err' ? 'alert-circle' : 'info');
    el.innerHTML = icon(iconName) + '<span>' + escapeHtml(msg) + '</span>';
    host.appendChild(el);

    var life = typeof ms === 'number' ? ms : 2000;
    global.setTimeout(function () {
      el.className += ' is-out';
      global.setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 220);
    }, life);
    return el;
  }

  /* ------------------------------------------------------------------ *
   * 组件二：一键复制
   * ------------------------------------------------------------------ */

  /** 复制到剪贴板：优先 Clipboard API，失败降级 execCommand */
  function writeClipboard(text) {
    if (global.navigator && global.navigator.clipboard && global.isSecureContext) {
      return global.navigator.clipboard.writeText(text).then(function () { return true; });
    }
    return new Promise(function (resolve, reject) {
      try {
        var ta = doc.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', 'readonly');
        ta.style.cssText = 'position:fixed;top:-1000px;left:-1000px;opacity:0;';
        doc.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, ta.value.length);
        var ok = doc.execCommand('copy');
        doc.body.removeChild(ta);
        ok ? resolve(true) : reject(new Error('COPY_FAILED'));
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * 复制文本 + 按钮态反馈 + 轻提示
   * @param {string} text
   * @param {HTMLElement} [btn] 触发的按钮，会短暂变成「已复制」态
   */
  function copy(text, btn) {
    text = (text == null ? '' : String(text)).trim();
    if (!text || text === '—') return Promise.resolve(false);

    return writeClipboard(text).then(function () {
      toast(t('ui.copied', '已复制'), 'ok', 1600);
      if (btn) pulseDone(btn);
      return true;
    }).catch(function () {
      toast(t('ui.copyFail', '复制失败，请手动选择文本'), 'err', 2400);
      return false;
    });
  }

  function pulseDone(btn) {
    var old = btn.innerHTML;
    var oldCls = btn.className;
    btn.className = oldCls + ' is-done';
    btn.innerHTML = icon('check') + '<span>' + escapeHtml(t('ui.copied', '已复制')) + '</span>';
    btn.setAttribute('aria-live', 'polite');
    global.setTimeout(function () {
      btn.className = oldCls;
      btn.innerHTML = old;
    }, 1600);
  }

  /* ------------------------------------------------------------------ *
   * 组件三：分段控制器 / 选项卡
   * ------------------------------------------------------------------ */

  /**
   * 把一段 HTML 变成可切换的选项卡
   * @param {HTMLElement} root     包含 .tool-tab 与 .tool-panel 的容器
   * @param {object}      [opts]   { onChange(mode) }
   */
  function segmented(root, opts) {
    if (!root) return null;
    opts = opts || {};
    var tabs = qa('.tool-tab', root);
    var panels = qa('.tool-panel', root);
    if (!tabs.length) return null;

    /* a11y（axe: aria-required-parent，critical）：role=tab 的元素必须有一个 role=tablist 的父级。
       各 partial 只写了 <div class="tool-tabs">，在共享层统一补，不必逐个模板改。 */
    var tablist = tabs[0].parentNode;
    if (tablist && tablist.getAttribute('role') !== 'tablist') {
      tablist.setAttribute('role', 'tablist');
    }

    function activate(mode, focus) {
      tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-mode') === mode;
        tab.className = on ? 'tool-tab is-active' : 'tool-tab';
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
        if (on && focus) tab.focus();
      });
      panels.forEach(function (panel) {
        var on = panel.getAttribute('data-panel') === mode;
        panel.className = on ? 'tool-panel is-active' : 'tool-panel';
        panel.hidden = !on;
      });
      if (typeof opts.onChange === 'function') opts.onChange(mode);
    }

    tabs.forEach(function (tab, i) {
      tab.setAttribute('role', 'tab');
      tab.addEventListener('click', function () {
        activate(tab.getAttribute('data-mode'));
      });
      // 键盘左右键切换（可访问性）
      tab.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        activate(next.getAttribute('data-mode'), true);
      });
    });

    var first = tabs.filter(function (x) { return x.className.indexOf('is-active') > -1; })[0] || tabs[0];
    activate(first.getAttribute('data-mode'));

    return { activate: activate };
  }

  /* ------------------------------------------------------------------ *
   * 组件四：主题切换
   * ------------------------------------------------------------------ */
  var THEME_KEY = 'tb-theme';

  var theme = {
    current: function () {
      return doc.documentElement.getAttribute('data-theme') || 'light';
    },
    apply: function (mode, persist) {
      doc.documentElement.setAttribute('data-theme', mode === 'dark' ? 'dark' : 'light');
      if (persist) {
        try { global.localStorage.setItem(THEME_KEY, mode); } catch (e) { /* 隐私模式忽略 */ }
      }
      var btn = q('[data-tool-ui="theme"]');
      if (btn) {
        btn.setAttribute('title', mode === 'dark' ? t('ui.themeToLight', '切换到浅色') : t('ui.themeToDark', '切换到深色'));
        btn.setAttribute('aria-label', btn.getAttribute('title'));
      }
    },
    toggle: function () {
      var next = this.current() === 'dark' ? 'light' : 'dark';
      this.apply(next, true);
      toast(next === 'dark' ? t('ui.themeDark', '已切换到深色模式') : t('ui.themeLight', '已切换到浅色模式'), 'info', 1500);
    },
    init: function () {
      this.apply(this.current(), false);
      var btn = q('[data-tool-ui="theme"]');
      if (btn) {
        var self = this;
        btn.addEventListener('click', function () { self.toggle(); });
      }
    }
  };

  /* ------------------------------------------------------------------ *
   * 组件五：回到顶部
   * ------------------------------------------------------------------ */
  function backTop() {
    qa('[data-tool-ui="backTop"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        global.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
    var threshold = 300;
    var CIRC = 138.2; // 2π×22，进度环周长（与 CSS stroke-dasharray 一致）
    var lastPct = -1;
    function sync() {
      var top = global.pageYOffset || doc.documentElement.scrollTop || 0;
      var show = top > threshold;
      var max = doc.documentElement.scrollHeight - global.innerHeight;
      var pct = max > 0 ? Math.min(Math.max(top / max, 0), 1) : 0;
      // 同值零写入：滚动高频触发，进度未变时跳过样式更新
      var pctChanged = pct !== lastPct;
      if (pctChanged) lastPct = pct;
      qa('[data-tool-ui="backTop"]').forEach(function (btn) {
        btn.className = btn.className.replace(/\s*is-show/, '') + (show ? ' is-show' : '');
        if (pctChanged) {
          var ring = btn.querySelector('.tool-float-ring-pg');
          if (ring) ring.style.strokeDashoffset = (CIRC * (1 - pct)).toFixed(1);
        }
      });
    }
    global.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* ------------------------------------------------------------------ *
   * 组件六：首页搜索 + 分类筛选（纯前端，SEO 内容仍在 DOM 中）
   * ------------------------------------------------------------------ */

  /**
   * 结构约定：
   *   [data-tool-search]                        搜索输入框
   *   [data-tool-search-clear]                  清空按钮
   *   [data-tool-chip][data-cat]                分类胶囊（data-cat="all" 为全部）
   *   [data-tool-group][data-cat]               分组区块
   *   .tool-grid-item[data-cat][data-search]    工具卡片，data-search 为可搜索文本
   *   [data-tool-empty]                         空状态
   *   [data-tool-count]                         结果计数
   */
  function homeFilter() {
    var input = q('[data-tool-search]');
    var tiles = qa('.tool-grid-item[data-search]');
    var groups = qa('[data-tool-group]');
    var chips = qa('[data-tool-chip]');
    var empty = q('[data-tool-empty]');
    var clearBtn = q('[data-tool-search-clear]');
    var hots = qa('[data-tool-hot]');
    var resetBtn = q('[data-tool-reset]');
    if (!input || !tiles.length) return;

    var state = { kw: '', cat: 'all' };

    function match(tile) {
      if (state.cat !== 'all' && tile.getAttribute('data-cat') !== state.cat) return false;
      if (!state.kw) return true;
      return (tile.getAttribute('data-search') || '').indexOf(state.kw) > -1;
    }

    function render() {
      // 我的收藏 / 最近使用两行在筛选激活时整体让位（CSS 按 body 类隐藏），不干扰结果区
      if (doc.body) doc.body.classList.toggle('tool-filtering', !!state.kw || state.cat !== 'all');
      var visible = 0;
      var catVisible = {};   // 一次遍历同时统计「哪些分类还有可见项」，省掉每组一次选择器查询
      tiles.forEach(function (tile) {
        var ok = match(tile);
        if (ok) {
          visible++;
          catVisible[tile.getAttribute('data-cat')] = 1;
        }
        // 同状态零 DOM 写入：className 重写会触发样式重算，状态没变就不碰
        if (ok === !/is-hidden/.test(tile.className)) return;
        tile.className = tile.className.replace(/\s*is-hidden/, '') + (ok ? '' : ' is-hidden');
      });
      groups.forEach(function (group) {
        var has = !!catVisible[group.getAttribute('data-cat')];
        if (group.hidden !== !has) group.hidden = !has;
      });
      if (empty) empty.className = empty.className.replace(/\s*is-show/, '') + (visible ? '' : ' is-show');
      if (clearBtn) clearBtn.className = clearBtn.className.replace(/\s*is-show/, '') + (state.kw ? ' is-show' : '');
      var counter = q('[data-tool-count]');
      if (counter) counter.textContent = String(visible);
    }

    // 刻意保持同步渲染：试过用 requestAnimationFrame 合帧，但输入事件本身稀疏
    // （打字间隔远大于一帧 16ms），合帧几乎合并不到任何东西，却让「派发 input 后立即读 DOM」
    // 的外部调用（冒烟规格、热词点击）拿到旧状态。收益不值，改回同步。
    input.addEventListener('input', function () {
      state.kw = String(input.value || '').trim().toLowerCase();
      render();
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { input.value = ''; state.kw = ''; render(); return; }
      // 回车直达：命中结果唯一时直接进工具页；多个匹配时不动作，避免误跳
      if (e.key === 'Enter' && !e.isComposing) {
        var hit = soleHit();
        if (hit) {
          e.preventDefault();
          goto(hit.getAttribute('href') || '');
        }
      }
    });
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = ''; state.kw = ''; input.focus(); render();
      });
    }
    /** 命中结果唯一时返回该卡片（回车直达用） */
    function soleHit() {
      var hit = tiles.filter(function (tile) { return !/is-hidden/.test(tile.className); });
      return hit.length === 1 ? hit[0] : null;
    }

    /** 派发可取消的跳转意图（tool:navigate），未被上层拦截时才真正跳转 */
    function goto(href) {
      if (!href) return;
      var ev = new global.CustomEvent('tool:navigate', {
        bubbles: true, cancelable: true, detail: { href: href }
      });
      if (global.document.dispatchEvent(ev) === false) return;
      global.location.href = href;
    }

    /** 分类复位到「全部」：点击热门词前先复位，避免「已选分类 + 热词」组合出空结果 */
    function resetCat() {
      state.cat = 'all';
      chips.forEach(function (c) {
        c.className = c.className.replace(/\s*is-active/, '') +
          (c.getAttribute('data-cat') === 'all' ? ' is-active' : '');
      });
    }

    /** 滚到第一个命中的分组（.tool-section 已配 scroll-margin-top，自然不会钻到吸顶导航下） */
    function scrollToFirstHit() {
      var first = q('.tool-grid-item:not(.is-hidden)');
      var section = first && first.closest ? first.closest('.tool-section') : null;
      if (section && typeof section.scrollIntoView === 'function') {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /** 把关键词写进输入框并即时筛选；scroll=true 时滚到结果（点击热门词的场景） */
    function applyKeyword(word, scroll) {
      input.value = word;
      state.kw = String(word || '').trim().toLowerCase();
      render();
      if (scroll) scrollToFirstHit();
    }

    // 热门搜索词：首屏就能点走，省去打字；分类一并复位，保证「点了就有结果」
    hots.forEach(function (btn) {
      btn.addEventListener('click', function () {
        resetCat();
        applyKeyword(btn.getAttribute('data-tool-hot') || '', true);
        input.focus();
      });
    });

    // 无结果时的「清空筛选条件」：一次复位关键词与分类，不留死胡同
    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        input.value = '';
        state.kw = '';
        resetCat();
        render();
        input.focus();
      });
    }

    // 键盘惯例：按 / 聚焦搜索框（已在输入态时不拦截，否则打不出斜杠）
    doc.addEventListener('keydown', function (e) {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      var el = doc.activeElement;
      var tag = el ? String(el.tagName || '').toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (el && el.isContentEditable)) return;
      e.preventDefault();
      input.focus();
    });

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        state.cat = chip.getAttribute('data-cat') || 'all';
        chips.forEach(function (c) {
          c.className = c.className.replace(/\s*is-active/, '') +
            (c === chip ? ' is-active' : '');
        });
        render();
      });
    });

    render();
  }

  /* ------------------------------------------------------------------ *
   * 组件七：计算历史（localStorage，最多保留 N 条）
   * ------------------------------------------------------------------ */
  var RECENT_MAX = 8;

  /**
   * @param {string} key      存储键（一般用工具 slug）
   * @param {object} [opts]   { list: HTMLElement, card: HTMLElement, render(item) }
   */
  function recent(key, opts) {
    opts = opts || {};
    var storeKey = 'tb-recent:' + key;
    var listEl = opts.list || q('[data-tool-recent-list="' + key + '"]');
    var cardEl = opts.card || q('[data-tool-recent="' + key + '"]');
    var clearBtn = q('[data-tool-recent-clear="' + key + '"]');

    function load() {
      try {
        var raw = global.localStorage.getItem(storeKey);
        var arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
      } catch (e) { return []; }
    }
    function save(arr) {
      try { global.localStorage.setItem(storeKey, JSON.stringify(arr.slice(0, RECENT_MAX))); } catch (e) { /* 忽略 */ }
    }

    function paint() {
      var arr = load();
      if (!listEl) return;
      if (!arr.length) {
        if (cardEl) cardEl.hidden = true;
        listEl.innerHTML = '';
        return;
      }
      if (cardEl) cardEl.hidden = false;
      listEl.innerHTML = arr.map(function (item) {
        var label = escapeHtml(item.label || '');
        var value = escapeHtml(item.value == null ? '' : item.value);
        return '<li class="tool-recent-item"><span class="tool-recent-label">' + label +
          '</span><span class="tool-recent-value">' + value + '</span></li>';
      }).join('');
    }

    if (cardEl) cardEl.hidden = true;
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        try { global.localStorage.removeItem(storeKey); } catch (e) { /* 忽略 */ }
        paint();
        toast(t('ui.historyCleared', '已清空历史记录'), 'ok', 1500);
      });
    }
    paint();

    return {
      push: function (item) {
        if (!item || item.value == null || item.value === '') return;
        var arr = load().filter(function (x) {
          return !(x.label === item.label && x.value === item.value);
        });
        arr.unshift({ label: item.label || '', value: item.value, ts: Date.now() });
        save(arr);
        paint();
      },
      clear: function () {
        try { global.localStorage.removeItem(storeKey); } catch (e) { /* 忽略 */ }
        paint();
      },
      paint: paint
    };
  }

  /* ------------------------------------------------------------------ *
   * 组件七·二：我的收藏 + 最近使用（localStorage，首页聚合展示）
   * ------------------------------------------------------------------ *
   * 结构约定（首页 index/index.html）：
   *   [data-tool-favs]         收藏行容器（默认 hidden，有收藏才显示）
   *   [data-tool-favs-list]    收藏 chips 容器
   *   [data-tool-visits]       最近使用行容器
   *   [data-tool-visits-list]  最近使用 chips 容器
   *   [data-tool-visits-clear] 「清空」按钮（批量删除）
   * 工具详情页：
   *   [data-tool-page]         slug 埋点（进入页面即记录最近使用）
   *   [data-tool-fav]          收藏按钮（aria-pressed 两态，星形 is-on 实心）
   * 存储：tools_favs = [slug…]（上限 24）、tools_recent = [slug…]（上限 8，前 = 最新）。
   * 数据只在用户浏览器里，服务端不落任何个性化内容，与「不设账号、不留痕迹」口径一致。
   */
  var FAV_KEY = 'tools_favs';
  var VISIT_KEY = 'tools_recent';
  var FAV_MAX = 24;
  var VISIT_MAX = 8;

  function readStore(key) {
    try {
      var raw = global.localStorage.getItem(key);
      var arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return [];
      return arr.filter(function (x) { return typeof x === 'string' && x; });
    } catch (e) { return []; }
  }

  function writeStore(key, arr) {
    try { global.localStorage.setItem(key, JSON.stringify(arr)); } catch (e) { /* 隐私模式忽略 */ }
  }

  /** 当前工具页 slug（无埋点 = 非工具页） */
  function pageSlug() {
    var mark = q('[data-tool-page]');
    return mark ? String(mark.getAttribute('data-tool-page') || '') : '';
  }

  /** 从首页工具卡收集 slug → {href, name, icon}（链接与图标直接复用卡片自身，零额外下发） */
  function toolMetas() {
    var map = {};
    qa('.tool-grid-item').forEach(function (a) {
      var href = a.getAttribute('href') || '';
      var m = href.match(/\/([^\/?#]+)\/?$/);
      if (!m) return;
      var use = a.querySelector('use');
      var nameEl = a.querySelector('.tool-tile-name');
      map[m[1]] = {
        href: href,
        name: nameEl ? String(nameEl.textContent || '').trim() : m[1],
        icon: use ? String(use.getAttribute('href') || '').replace(/^#/, '') : 'i-grid'
      };
    });
    return map;
  }

  /** 渲染一排 chips：a（进工具）与 button（移除）平级，避免交互元素嵌套（axe nested-interactive） */
  function paintChips(box, slugs, metas, ariaTpl) {
    box.innerHTML = slugs.map(function (slug) {
      var meta = metas[slug] || { href: slug, name: slug, icon: 'i-grid' };
      var aria = String(ariaTpl || '').replace('{n}', meta.name);
      return '<span class="tool-personal-chip">' +
        '<a class="tool-personal-link" href="' + escapeHtml(meta.href) + '">' +
          icon(String(meta.icon).replace(/^i-/, '')) +
          '<span class="tool-personal-name">' + escapeHtml(meta.name) + '</span>' +
        '</a>' +
        '<button class="tool-personal-x" type="button" data-x="' + escapeHtml(slug) + '" aria-label="' + escapeHtml(aria) + '">' +
          icon('x') +
        '</button>' +
      '</span>';
    }).join('');
  }

  /** 记录最近使用：进入工具页即调用；已在首位时零写入 */
  function recordVisit(slug) {
    if (!slug) return;
    var arr = readStore(VISIT_KEY);
    var i = arr.indexOf(slug);
    if (i === 0) return;
    if (i > -1) arr.splice(i, 1);
    arr.unshift(slug);
    writeStore(VISIT_KEY, arr.slice(0, VISIT_MAX));
  }

  /** 我的收藏：工具页按钮两态 + 首页聚合行（单例工厂：重复调用返回同一实例） */
  var favsInstance = null;

  function favs() {
    if (favsInstance) return favsInstance;
    var section = q('[data-tool-favs]');
    var box = q('[data-tool-favs-list]');
    var btn = q('[data-tool-fav]');
    var btnText = btn ? btn.querySelector('[data-tool-fav-text]') : null;
    if (!section && !btn) return;

    var metas = section ? toolMetas() : {};

    function list() { return readStore(FAV_KEY); }
    function save(arr) { writeStore(FAV_KEY, arr.slice(0, FAV_MAX)); }
    function has(slug) { return list().indexOf(slug) > -1; }
    function add(slug) {
      var arr = list();
      var i = arr.indexOf(slug);
      if (i > -1) arr.splice(i, 1);
      arr.unshift(slug);
      save(arr);
    }
    function remove(slug) {
      var arr = list();
      var i = arr.indexOf(slug);
      if (i > -1) { arr.splice(i, 1); save(arr); }
    }
    function toggle(slug) {
      if (has(slug)) { remove(slug); return false; }
      add(slug);
      return true;
    }

    /** 同步工具页按钮两态（同值零 DOM 写入） */
    function syncBtn() {
      if (!btn) return;
      var slug = pageSlug();
      if (!slug) return;
      var on = has(slug);
      if (btn.getAttribute('aria-pressed') !== String(on)) btn.setAttribute('aria-pressed', String(on));
      var txt = on ? t('ui.favAdded', '已收藏') : t('ui.favAdd', '收藏');
      if (btnText && btnText.textContent !== txt) btnText.textContent = txt;
      if (btn.getAttribute('title') !== txt) btn.setAttribute('title', txt);
      var next = btn.className.replace(/\s*is-on/, '') + (on ? ' is-on' : '');
      if (btn.className !== next) btn.className = next;
    }

    function paint() {
      syncBtn();
      if (!section || !box) return;
      var arr = list();
      var show = arr.length > 0;
      if (section.hidden !== !show) section.hidden = !show;
      if (!show) return;
      var mark = arr.join(',');
      if (box.__favs === mark) return; // 同状态零重建
      box.__favs = mark;
      paintChips(box, arr, metas, t('ui.favRemoveAria', '从我的收藏移除{n}'));
    }

    if (box) {
      box.addEventListener('click', function (ev) {
        var x = ev.target && ev.target.closest ? ev.target.closest('.tool-personal-x') : null;
        if (!x) return;
        remove(x.getAttribute('data-x') || '');
        paint();
        toast(t('ui.favRemoved', '已从我的收藏移除'), 'ok', 1500);
      });
    }
    if (btn) {
      btn.addEventListener('click', function () {
        var slug = pageSlug();
        if (!slug) return;
        var on = toggle(slug);
        syncBtn();
        toast(on ? t('ui.favAddedToast', '已加入我的收藏') : t('ui.favRemoved', '已从我的收藏移除'), 'ok', 1500);
      });
    }
    paint();

    favsInstance = { paint: paint, add: add, remove: remove, toggle: toggle, has: has, list: list };
    return favsInstance;
  }

  /** 最近使用：首页聚合行（单删 + 一键清空）；记录入口是 recordVisit（单例工厂同上） */
  var visitsInstance = null;

  function visits() {
    if (visitsInstance) return visitsInstance;
    var section = q('[data-tool-visits]');
    var box = q('[data-tool-visits-list]');
    var clearBtn = q('[data-tool-visits-clear]');
    if (!section || !box) return;

    var metas = toolMetas();

    function list() { return readStore(VISIT_KEY); }
    function removeOne(slug) {
      var arr = list();
      var i = arr.indexOf(slug);
      if (i > -1) { arr.splice(i, 1); writeStore(VISIT_KEY, arr); }
      return list();
    }
    function clear() { writeStore(VISIT_KEY, []); }

    function paint() {
      var arr = list();
      var show = arr.length > 0;
      if (section.hidden !== !show) section.hidden = !show;
      if (!show) return;
      var mark = arr.join(',');
      if (box.__visits === mark) return; // 同状态零重建
      box.__visits = mark;
      paintChips(box, arr, metas, t('ui.visitRemoveAria', '从最近使用移除{n}'));
    }

    box.addEventListener('click', function (ev) {
      var x = ev.target && ev.target.closest ? ev.target.closest('.tool-personal-x') : null;
      if (!x) return;
      removeOne(x.getAttribute('data-x') || '');
      paint();
      toast(t('ui.visitRemoved', '已从最近使用移除'), 'ok', 1500);
    });
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        clear();
        paint();
        toast(t('ui.visitCleared', '已清空最近使用'), 'ok', 1500);
      });
    }
    paint();

    visitsInstance = { paint: paint, remove: removeOne, clear: clear, list: list };
    return visitsInstance;
  }

  /* 单例未初始化时（如脚本加载后 DOM 未就绪），转发方法惰性触发首次创建。
     这样 ToolUI.favs.paint() / ToolUI.favs() 两种调用形态都可用（冒烟规格依赖前者）。 */
  ['paint', 'add', 'remove', 'toggle', 'has', 'list'].forEach(function (k) {
    favs[k] = function () {
      var a = favs();
      return a && a[k] ? a[k].apply(a, arguments) : undefined;
    };
  });
  ['paint', 'remove', 'clear', 'list'].forEach(function (k) {
    visits[k] = function () {
      var a = visits();
      return a && a[k] ? a[k].apply(a, arguments) : undefined;
    };
  });

  /* ------------------------------------------------------------------ *
   * 组件八：结果值变化时的高亮脉冲
   * ------------------------------------------------------------------ */
  function pulse(el) {
    if (!el) return;
    el.className = el.className.replace(/\s*is-pulse/, '');
    void el.offsetWidth; // 强制重排以重启动画
    el.className += ' is-pulse';
  }

  /* ------------------------------------------------------------------ *
   * 组件九：数值步进器（左 − 右 +，纯鼠标调值，避免键鼠切换中断）
   * 结构：<span class="tool-stepper">
   *         <button class="tool-step" data-step="down|up" data-for="inputId">
   *         <input class="tool-input" id="inputId">
   *         <button ...>
   *       </span>
   * 可选 data 属性（写在 input 上）：
   *   data-step-cap    步长上限（如利率 0.1、百分比 1、密码长度 1）
   *   data-step-min    最小值钳制（如密码长度 4）
   *   data-step-max    最大值钳制（如密码长度 128）
   *   data-step-scale  步长档位（默认数量级：<1→0.1, ≥1→1, ≥100→10, ≥1000→100）
   * 触发时机：点击后回调 onChange(inputEl)（各工具在此实时重算）。
   * ------------------------------------------------------------------ */
  function stepper(rootEl, onChange) {
    if (!rootEl || !onChange) return;
    rootEl.addEventListener('click', function (ev) {
      var btn = ev.target && ev.target.closest ? ev.target.closest('.tool-step') : null;
      if (!btn) return;
      var el = q('#' + btn.getAttribute('data-for'));
      if (!el) return;
      var raw = String(el.value).trim().replace(/,/g, '');
      var valid = raw !== '' && isFinite(Number(raw));
      var v = valid ? Number(raw) : 0;
      var cap = parseFloat(btn.getAttribute('data-step-cap') || el.getAttribute('data-step-cap') || '');
      var min = parseFloat(btn.getAttribute('data-step-min') || el.getAttribute('data-step-min') || '');
      var max = parseFloat(btn.getAttribute('data-step-max') || el.getAttribute('data-step-max') || '');
      var scale = parseFloat(btn.getAttribute('data-step-scale') || el.getAttribute('data-step-scale') || '');
      var s;
      if (valid) {
        var n = Math.abs(v);
        if (isFinite(scale) && scale > 0) {
          s = scale;   /* 显式档位（如贷款金额按 1 万步进） */
        } else {
          s = n >= 1000 ? 100 : n >= 100 ? 10 : n >= 1 ? 1 : 0.1;
        }
        if (isFinite(cap)) s = Math.min(s, cap);
      } else {
        s = isFinite(cap) && cap > 0 ? cap : (isFinite(scale) && scale > 0 ? scale : 1);   /* 非法输入从可用步长起步 */
      }
      var next = Math.round((v + (btn.getAttribute('data-step') === 'up' ? 1 : -1) * s) * 1e6) / 1e6;
      if (isFinite(min) && next < min) next = min;
      if (isFinite(max) && next > max) next = max;
      el.value = String(next);
      pulse(el);
      onChange(el, btn);
    });
  }

  /* ------------------------------------------------------------------ *
   * 组件十：受控日期选择器（显示恒为 YYYY-MM-DD 横杠格式，符合中文习惯）
   * 结构：<input class="tool-input" data-tool-datepicker placeholder="2026-09-11">
   * 行为：输入框可直接键入（自动纠正 / . 空格分隔为 -）；聚焦弹出日历面板：
   *   - 头部「年份 / 月份」下拉直达任意月份（选历史生日不必逐月翻页）
   *   - 键盘导航：←→ ±1 天，↑↓ ±7 天，Home/End 到周一/周日，
   *     PageUp/PageDown ±1 月（加 Shift ±1 年），Enter 选中，Esc 关闭
   *   - 页面滚动 / 缩放 / 点击面板外自动关闭（面板绝对定位，滚动会错位）
   *   - 视口空间不足时翻转到输入框上方；右缘防溢出
   *   - 整页共享一个面板（单例），焦点 Tab 离开输入框即收起
   * 选择后写回 input 并派发 input + change 事件，工具页现有监听无需改动。
   * 文案取 i18n：ui.dpToday / ui.dpWeek / ui.dpLabel / ui.dpPrev / ui.dpNext。
   * ------------------------------------------------------------------ */
  function datePicker(rootEl) {
    rootEl = rootEl || doc;
    var inputs = rootEl.querySelectorAll ? rootEl.querySelectorAll('input[data-tool-datepicker]') : [];
    if (!inputs.length) return;

    var WEEK = t('ui.dpWeek', ['一', '二', '三', '四', '五', '六', '日']);
    var TODAY_LABEL = t('ui.dpToday', '今天');
    var TOMORROW_LABEL = t('ui.dpTomorrow', '明天');
    var WEEKEND_LABEL = t('ui.dpWeekend', '本周末');
    var NEXTWEEK_LABEL = t('ui.dpNextWeek', '下周');
    var PREV_LABEL = t('ui.dpPrev', '上个月');
    var NEXT_LABEL = t('ui.dpNext', '下个月');
    var TITLE_LABEL = t('ui.dpLabel', '选择日期');
    var YEAR_LABEL = t('ui.dpYearLabel', '年份');
    var MONTH_LABEL = t('ui.dpMonthLabel', '月份');
    var YEAR_MIN = 1900;
    var YEAR_MAX = 2100;

    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function isoOf(y, m, d) { return y + '-' + pad(m) + '-' + pad(d); }
    function parseIso(s) {
      var m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(String(s || '').trim());
      if (!m) return null;
      var y = +m[1], mo = +m[2], d = +m[3];
      if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
      var dt = new Date(y, mo - 1, d);
      if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
      return { y: y, m: mo, d: d };
    }
    /** 用户键入 / . 空格 分隔时自动纠正为 -（仅在整体合法时替换） */
    function normalize(v) {
      var s = String(v || '').trim().replace(/[\/.．年月\s]+/g, '-').replace(/-+$/g, '').replace(/日/g, '');
      var m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
      if (!m) return null;
      var p = parseIso(isoOf(+m[1], +m[2], +m[3]));
      return p ? isoOf(p.y, p.m, p.d) : null;
    }

    /* ---- S1 农历数据层（zhkit + 节假日）：首次打开面板时懒加载，失败静默降级 ----
       13 个日期页共用本组件，但 zhkit.js（gzip 约 23KB）+ holiday-data.js 不值得全站静态引入；
       懒加载后浏览器缓存生效，同页二次打开零成本。加载失败时面板退化为纯公历，
       选日期主流程不受影响（断网首次访问仍可用，二次访问走 HTTP 缓存恢复农历层）。 */
    var ZHKIT_SRC = '/static/tools/engine/zhkit.js';
    var HOLIDAY_SRC = '/static/tools/engine/holiday-data.js';
    var lunarTried = false;
    function hasLunar() { return !!(global.ToolEngine && global.ToolEngine.zhkit); }
    function ensureLunar() {
      if (lunarTried || hasLunar()) return;
      lunarTried = true;
      var need = [];
      if (!hasLunar()) need.push(ZHKIT_SRC);
      if (!global.ToolHoliday) need.push(HOLIDAY_SRC);
      if (!need.length) return;
      var left = need.length;
      var done = function () {
        if (--left > 0) return;
        /* 有任一数据层仍缺失 → 允许下次打开重试（瞬时网络抖动不该永久禁用农历层） */
        if (!hasLunar() || !global.ToolHoliday) { lunarTried = false; }
        /* 数据到位后重绘当前面板（面板已关闭则跳过，下次打开自然带上） */
        if (cur && panel && !panel.hidden) { render(); place(); }
      };
      for (var i = 0; i < need.length; i++) {
        var s = doc.createElement('script');
        s.src = need[i];
        s.onload = done;
        s.onerror = done;
        doc.head.appendChild(s);
      }
    }
    /* 农历查询缓存：翻一个月约 31 次查询，同一 iso 只算一次 */
    var lunarCache = {};
    function lunarOf(iso) {
      if (Object.prototype.hasOwnProperty.call(lunarCache, iso)) return lunarCache[iso];
      var r = null;
      try { r = global.ToolEngine.zhkit.solarToLunar(iso); } catch (e) { r = null; }
      var size = 0, k;
      for (k in lunarCache) { size++; }
      if (size > 400) { lunarCache = {}; }
      lunarCache[iso] = r;
      return r;
    }
    /** 展开 [from, to] 闭区间为 iso 列表（节假日跨度 → 逐日映射用） */
    function eachDay(from, to, fn) {
      var a = parseIso(from), b = parseIso(to);
      if (!a || !b) return;
      var d = new Date(a.y, a.m - 1, a.d), end = new Date(b.y, b.m - 1, b.d);
      while (d <= end) {
        fn(isoOf(d.getFullYear(), d.getMonth() + 1, d.getDate()));
        d.setDate(d.getDate() + 1);
      }
    }

    /* ---- 单例面板与状态（整页共享，避免每个输入框各建面板和全局监听） ---- */
    var panel = null, yearSel = null, monthSel = null;
    var cur = null;        // 当前打开的 { input, view:{y,m}, cursor:{y,m,d} }
    var lastView = null;   // 上次浏览的月份：重开空输入框时延续，翻页不丢位置
    var closeTimer = null; // blur 延迟关闭定时器（重新聚焦时取消，避免误关）

    function ensurePanel() {
      if (panel && doc.body.contains(panel)) return;
      panel = doc.createElement('div');
      panel.className = 'tool-dp';
      panel.hidden = true;
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-label', TITLE_LABEL);
      doc.body.appendChild(panel);

      /* 年份 / 月份快速跳转下拉 */
      yearSel = doc.createElement('select');
      yearSel.className = 'tool-dp-year';
      yearSel.setAttribute('aria-label', YEAR_LABEL);
      for (var y = YEAR_MIN; y <= YEAR_MAX; y++) {
        var oy = doc.createElement('option');
        oy.value = String(y);
        oy.textContent = String(y);
        yearSel.appendChild(oy);
      }
      yearSel.addEventListener('change', function () {
        if (!cur) return;
        cur.view.y = Math.min(YEAR_MAX, Math.max(YEAR_MIN, +yearSel.value));
        lastView = { y: cur.view.y, m: cur.view.m };
        render();
      });

      monthSel = doc.createElement('select');
      monthSel.className = 'tool-dp-month';
      monthSel.setAttribute('aria-label', MONTH_LABEL);
      for (var mo = 1; mo <= 12; mo++) {
        var om = doc.createElement('option');
        om.value = String(mo);
        om.textContent = mo + '月';
        monthSel.appendChild(om);
      }
      monthSel.addEventListener('change', function () {
        if (!cur) return;
        cur.view.m = +monthSel.value;
        lastView = { y: cur.view.y, m: cur.view.m };
        render();
      });

      /* 面板内点击（事件委托：快捷项 / 翻月 / 选日） */
      panel.addEventListener('click', function (ev) {
        if (!cur) return;
        var btn = ev.target && ev.target.closest ? ev.target.closest('[data-dp], [data-dp-day], [data-dp-quick]') : null;
        if (!btn) return;
        /* 阻止冒泡到 document 的"面板外点击关闭"监听：
           render() 重建 innerHTML 后按钮已脱离 DOM，closest('.tool-dp') 失配会被误判为面板外点击而误关 */
        ev.stopPropagation();
        var qIso = btn.getAttribute('data-dp-quick');
        if (qIso) { commit(qIso); return; }
        var act = btn.getAttribute('data-dp');
        if (act === 'prev') { shiftView(0, -1); }
        else if (act === 'next') { shiftView(0, 1); }
        else if (btn.getAttribute('data-dp-day')) {
          commit(btn.getAttribute('data-dp-day'));
        }
      });

      /* 全局关闭：面板外点击 / 任何元素滚动（捕获） / 窗口缩放 */
      doc.addEventListener('click', function (ev) {
        if (!cur || panel.hidden) return;
        var et = ev.target;
        /* 优先用 composedPath（事件分发时的路径快照）判断是否面板内点击：
           面板内 render() 重建 innerHTML 后 target 已脱离 DOM，closest 沿父链会失配误判 */
        var inPanel = ev.composedPath
          ? ev.composedPath().indexOf(panel) !== -1
          : (et === panel || (et.closest && et.closest('.tool-dp')));
        if (inPanel || (et.closest && et.closest('input[data-tool-datepicker]'))) return;
        close();
      });
      doc.addEventListener('scroll', function () {
        if (cur && !panel.hidden) { close(); }
      }, true);
      global.addEventListener('resize', function () {
        if (cur && !panel.hidden) { close(); }
      });
    }

    function shiftView(years, months) {
      cur.view.y += years;
      cur.view.m += months;
      while (cur.view.m < 1) { cur.view.m += 12; cur.view.y--; }
      while (cur.view.m > 12) { cur.view.m -= 12; cur.view.y++; }
      cur.view.y = Math.min(YEAR_MAX, Math.max(YEAR_MIN, cur.view.y));
      lastView = { y: cur.view.y, m: cur.view.m };
      render();
    }

    function render() {
      var y = cur.view.y, m = cur.view.m;
      yearSel.value = String(y);
      monthSel.value = String(m);
      var first = new Date(y, m - 1, 1);
      var lead = (first.getDay() + 6) % 7;               // 周一=0 … 周日=6
      var days = new Date(y, m, 0).getDate();
      var now = new Date();
      var todayIso = isoOf(now.getFullYear(), now.getMonth() + 1, now.getDate());
      var sel = parseIso(cur.input.value);
      var c = cur.cursor;
      /* 快捷项（office-pro 风格）：今天 / 明天 / 本周末（本周日） / 下周（今天+7） */
      function quickIso(offsetDays) {
        var dt = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offsetDays);
        return isoOf(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
      }
      var dowSun0 = now.getDay();
      var weekendOffset = dowSun0 === 0 ? 0 : 7 - dowSun0;   /* 周日当天=今天，否则 upcoming 周日 */
      var QUICK = [
        { label: TODAY_LABEL, iso: quickIso(0) },
        { label: TOMORROW_LABEL, iso: quickIso(1) },
        { label: WEEKEND_LABEL, iso: quickIso(weekendOffset) },
        { label: NEXTWEEK_LABEL, iso: quickIso(7) }
      ];
      /* S1 农历层：节气表 + 节假日/调休映射。数据未就绪或年份超界（1900 视图）时为 null，
         面板退化为纯公历；zhkit.solarTerms 仅覆盖 1901~2100，与农历口径一致。 */
      var zk = hasLunar() ? global.ToolEngine.zhkit : null;
      var termMap = null;
      if (zk && y >= 1901 && y <= 2100) {
        try {
          termMap = {};
          zk.solarTerms(y).forEach(function (t) { termMap[t.iso] = t.name; });
        } catch (e) { termMap = null; }
      }
      var holMap = null, workMap = null;
      var hy = global.ToolHoliday && global.ToolHoliday.years ? global.ToolHoliday.years[y] : null;
      if (hy) {
        holMap = {}; workMap = {};
        (hy.holidays || []).forEach(function (h) {
          eachDay(h.from, h.to, function (iso) { holMap[iso] = h.name; });
        });
        (hy.workdays || []).forEach(function (w) { workMap[w] = true; });
      }
      var html = '<div class="tool-dp-head">' +
        '<button type="button" class="tool-dp-nav" data-dp="prev" aria-label="' + escapeHtml(PREV_LABEL) + '">‹</button>' +
        '<span class="tool-dp-title"></span>' +
        '<button type="button" class="tool-dp-nav" data-dp="next" aria-label="' + escapeHtml(NEXT_LABEL) + '">›</button></div>' +
        '<div class="tool-dp-quick">' + QUICK.map(function (qk) {
          return '<button type="button" class="tool-dp-qbtn" data-dp-quick="' + qk.iso + '">' + escapeHtml(qk.label) + '</button>';
        }).join('') + '</div>' +
        '<div class="tool-dp-week">' + WEEK.map(function (w) { return '<span>' + escapeHtml(w) + '</span>'; }).join('') + '</div>' +
        '<div class="tool-dp-grid">';
      var i, d;
      for (i = 0; i < lead; i++) html += '<span class="tool-dp-blank"></span>';
      for (d = 1; d <= days; d++) {
        var iso = isoOf(y, m, d);
        var cls = 'tool-dp-day';
        if (iso === todayIso) { cls += ' is-today'; }
        if (sel && sel.y === y && sel.m === m && sel.d === d) { cls += ' is-sel'; }
        if (c && c.y === y && c.m === m && c.d === d) { cls += ' is-cursor'; }
        var wd = new Date(y, m - 1, d).getDay();
        if (wd === 0 || wd === 6) { cls += ' is-weekend'; }   // 周末红字（纸历惯例）
        /* 标注：节气名优先于农历日（纸历惯例，如 10-08 显示「寒露」）；
           农历初一显示月名（如「正月」），其余显示日名（如「廿一」）；
           休/班角标取国务院安排（holiday-data.js，2024~2026），title 带节日名 */
        var label = '', badge = '', badgeTitle = '';
        if (termMap && termMap[iso]) { label = termMap[iso]; }
        else if (zk) {
          var lu = lunarOf(iso);
          if (lu) { label = lu.lunarDay === 1 ? lu.monthName : lu.dayName; }
        }
        if (holMap && holMap[iso]) { badge = '休'; badgeTitle = holMap[iso]; }
        else if (workMap && workMap[iso]) { badge = '班'; badgeTitle = '调休上班'; }
        var aria = iso + (label ? ' ' + label : '') + (badge ? ' ' + badgeTitle : '');
        var attrs = (iso === todayIso ? ' aria-current="date"' : '') + ' aria-label="' + escapeHtml(aria) + '"';
        var inner = '<span class="tool-dp-d">' + d + '</span>';
        if (label || badge) {
          inner += '<span class="tool-dp-l">' + escapeHtml(label) +
            (badge ? '<span class="tool-dp-b ' + (badge === '休' ? 'is-off' : 'is-work') + '" title="' + escapeHtml(badgeTitle) + '">' + badge + '</span>' : '') +
            '</span>';
        }
        html += '<button type="button" class="' + cls + '" data-dp-day="' + iso + '"' + attrs + '>' + inner + '</button>';
      }
      html += '</div>';
      /* 月份切换播报（屏幕阅读器）：文案随语言走——原先硬编码「年月」，
         英文站读屏会念出中文（视觉隐藏，肉眼扫不到）；兜底刻意写成中性的 {y}/{m} */
      html += '<span class="tool-dp-live" aria-live="polite">'
        + escapeHtml(t('ui.dpAnnounce', '{y}/{m}').replace('{y}', y).replace('{m}', m))
        + '</span>';
      panel.innerHTML = html;
      /* 年月下拉是持久节点，每次渲染后重新挂回标题 */
      var title = panel.querySelector('.tool-dp-title');
      title.appendChild(yearSel);
      title.appendChild(monthSel);
    }

    function place() {
      var r = cur.input.getBoundingClientRect();
      var vw = doc.documentElement.clientWidth || global.innerWidth;
      var vh = doc.documentElement.clientHeight || global.innerHeight;
      var w = panel.offsetWidth || 260;
      var h = panel.offsetHeight || 320;
      var left = r.left + global.scrollX;
      if (left + w > global.scrollX + vw - 8) { left = global.scrollX + vw - 8 - w; }   // 右缘防溢出
      left = Math.max(8, left);
      var top;
      if (r.bottom + 6 + h > vh) {                       // 下方空间不足 → 翻到输入框上方
        top = r.top + global.scrollY - 6 - h;
        if (top < global.scrollY) { top = r.bottom + global.scrollY + 6; }
      } else {
        top = r.bottom + global.scrollY + 6;
      }
      panel.style.left = left + 'px';
      panel.style.top = top + 'px';
      panel.style.minWidth = Math.max(r.width, 240) + 'px';
    }

    function open(input) {
      if (cur && cur.input === input && !panel.hidden) { return; }   // 已为该输入打开
      if (closeTimer) { global.clearTimeout(closeTimer); closeTimer = null; }   // 取消遗留的延迟关闭
      ensurePanel();
      ensureLunar();   // S1：首次打开时懒加载农历/节假日数据层，到位后重绘
      var sel = parseIso(input.value);
      var now = new Date();
      var v = sel ? { y: sel.y, m: sel.m }
        : (lastView ? { y: lastView.y, m: lastView.m } : { y: now.getFullYear(), m: now.getMonth() + 1 });
      cur = {
        input: input,
        view: v,
        cursor: sel ? { y: sel.y, m: sel.m, d: sel.d } : { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() }
      };
      panel.hidden = false;
      render();
      place();
    }

    function close() {
      if (panel) { panel.hidden = true; }
      cur = null;
    }

    function commit(iso) {
      var input = cur.input;
      close();            // 先收面板再写值派发，避免派发过程中重复渲染
      input.value = iso;
      pulse(input);
      input.dispatchEvent(new global.Event('input', { bubbles: true }));
      input.dispatchEvent(new global.Event('change', { bubbles: true }));
    }

    /* 键盘游标移动：Date 自动进位（9月0日=8月31日），视图跟随游标 */
    function moveCursor(y, m, d) {
      var dt = new Date(y, m - 1, d);
      var cy = dt.getFullYear();
      if (cy < YEAR_MIN || cy > YEAR_MAX) { return; }
      cur.cursor = { y: cy, m: dt.getMonth() + 1, d: dt.getDate() };
      if (cur.cursor.y !== cur.view.y || cur.cursor.m !== cur.view.m) {
        cur.view = { y: cur.cursor.y, m: cur.cursor.m };
        lastView = { y: cur.cursor.y, m: cur.cursor.m };
      }
      render();
    }

    function onKeyDown(ev) {
      if (!cur || panel.hidden) { return; }
      var c = cur.cursor;
      var k = ev.key;
      if (k === 'Escape') { ev.preventDefault(); close(); return; }
      if (k === 'Enter') { ev.preventDefault(); commit(isoOf(c.y, c.m, c.d)); return; }
      var dow = (new Date(c.y, c.m - 1, c.d).getDay() + 6) % 7;   // 周一=0 … 周日=6
      if (k === 'ArrowLeft') { ev.preventDefault(); moveCursor(c.y, c.m, c.d - 1); }
      else if (k === 'ArrowRight') { ev.preventDefault(); moveCursor(c.y, c.m, c.d + 1); }
      else if (k === 'ArrowUp') { ev.preventDefault(); moveCursor(c.y, c.m, c.d - 7); }
      else if (k === 'ArrowDown') { ev.preventDefault(); moveCursor(c.y, c.m, c.d + 7); }
      else if (k === 'Home') { ev.preventDefault(); moveCursor(c.y, c.m, c.d - dow); }
      else if (k === 'End') { ev.preventDefault(); moveCursor(c.y, c.m, c.d + (6 - dow)); }
      else if (k === 'PageUp') { ev.preventDefault(); moveCursor(c.y - (ev.shiftKey ? 1 : 0), c.m - (ev.shiftKey ? 0 : 1), c.d); }
      else if (k === 'PageDown') { ev.preventDefault(); moveCursor(c.y + (ev.shiftKey ? 1 : 0), c.m + (ev.shiftKey ? 0 : 1), c.d); }
    }

    Array.prototype.forEach.call(inputs, function (input) {
      if (input.getAttribute('data-dp-bound')) return;
      input.setAttribute('data-dp-bound', '1');
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('spellcheck', 'false');

      input.addEventListener('focus', function () { open(input); });
      input.addEventListener('click', function () {
        if (!cur || cur.input !== input) { open(input); }   // 滚动关闭后再次点击可重开
      });
      input.addEventListener('input', function () {
        var iso = normalize(input.value);
        if (iso && iso !== input.value) {
          var pos = input.selectionStart;
          input.value = iso;   // 键入 / 分隔自动纠正为 -；不合法不干预
          try { input.setSelectionRange(pos, pos); } catch (e) { /* 无选区 API 时忽略 */ }
        }
        if (cur && cur.input === input && !panel.hidden) {
          var typed = parseIso(input.value);
          if (typed && (typed.y !== cur.view.y || typed.m !== cur.view.m)) {
            cur.view = { y: typed.y, m: typed.m };   // 手输完整日期 → 视图跟随到该月
            cur.cursor = { y: typed.y, m: typed.m, d: typed.d };
          }
          render();   // 手输新值同步高亮
        }
      });
      input.addEventListener('keydown', onKeyDown);
      input.addEventListener('blur', function (ev) {
        var rt = ev.relatedTarget;
        if (rt && panel && panel.contains(rt)) { return; }   // 焦点移入面板不关闭
        if (closeTimer) { global.clearTimeout(closeTimer); }
        closeTimer = global.setTimeout(function () {
          closeTimer = null;
          if (cur && cur.input === input) { close(); }
        }, 120);   // 稍候片刻，让面板按钮的 click 先完成
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 全局 a11y 增强（对既有标记做一次性补齐）
   * ------------------------------------------------------------------ */
  function enhanceA11y() {
    // 可滚动区域必须可键盘聚焦（axe: scrollable-region-focusable，serious）。
    // .tool-schedule-scroll 分布在 38 个 partial 里，逐个改不现实，在这里统一补。
    qa('.tool-schedule-scroll').forEach(function (el) {
      if (!el.hasAttribute('tabindex')) { el.setAttribute('tabindex', '0'); }
    });
  }

  /* ------------------------------------------------------------------ *
   * 快捷示例折叠
   * ------------------------------------------------------------------ */

  /** 示例按钮超过首屏上限时，多余的收入「更多示例」折叠区（2026-09-13）。
   *  动因：示例对低上手用户是主入口，数量上不封顶；但一排按钮平铺超过
   *  6 个会折行挤压首屏。折叠不删减——隐藏按钮仍在 DOM 里，全站点击器
   *  照样点得到，快照覆盖不受影响。首屏条数可用 data-examples-visible 覆写。 */
  var EXAMPLES_VISIBLE_DEFAULT = 6;

  function examplesSync(box, limit) {
    var expanded = box.classList.contains('is-expanded');
    var btns = box.querySelectorAll('.tool-example');
    for (var i = 0; i < btns.length; i++) {
      btns[i].classList.toggle('tool-example--extra', i >= limit && !expanded);
    }
    var more = box.querySelector('.tool-examples-more');
    if (more) {
      var label = expanded
        ? t('ui.fewerExamples', '收起示例')
        : t('ui.moreExamples', '更多示例') + ' (' + (btns.length - limit) + ')';
      // 幂等铁律：textContent 赋值即使同值也会替换文本节点（childList 变化），
      // 无条件写会被下方 MutationObserver 自馈成死循环（2026-09-13 CPU 飙升根因），
      // 必须只在文案真变化时写入
      if (more.textContent !== label) { more.textContent = label; }
    }
  }

  function examplesApply(box) {
    var btns = box.querySelectorAll('.tool-example');
    var limit = parseInt(box.getAttribute('data-examples-visible'), 10);
    if (isNaN(limit) || limit <= 0) { limit = EXAMPLES_VISIBLE_DEFAULT; }
    var more = box.querySelector('.tool-examples-more');
    if (btns.length <= limit) {
      // 无需折叠：清掉模式切换后残留的折叠按钮与隐藏态
      if (more) { more.parentNode.removeChild(more); }
      box.classList.remove('is-expanded');
      for (var i = 0; i < btns.length; i++) { btns[i].classList.remove('tool-example--extra'); }
      return;
    }
    if (!more) {
      more = doc.createElement('button');
      more.type = 'button';
      more.className = 'tool-examples-more';
      more.addEventListener('click', function () {
        box.classList.toggle('is-expanded');
        examplesSync(box, limit);
      });
      box.appendChild(more);
    }
    examplesSync(box, limit);
  }

  function examplesCollapse() {
    qa('[data-examples]').forEach(examplesApply);
    // 模式切换会整体重渲染示例区（innerHTML 覆写），观察后续变化重新折叠。
    // 前提：examplesApply 必须幂等——同状态下零 DOM 写入（尤其不得无条件写
    // textContent），否则观察器自馈死循环（2026-09-13 曾因此 CPU 飙升）
    if (typeof global.MutationObserver !== 'function' || !doc.body) { return; }
    var mo = new MutationObserver(function (muts) {
      // 自馈熔断：处理期间 disconnect——本回调里 apply 产生的任何变化都不会
      // 再喂回自己（2026-09-13 CPU 飙升事故的机制性封死），处理完重新接上
      mo.disconnect();
      try {
        var last = null;
        for (var i = 0; i < muts.length; i++) {
          var tgt = muts[i].target;
          var box = tgt && tgt.closest ? tgt.closest('[data-examples]') : null;
          if (box && box !== last) { last = box; examplesApply(box); }
        }
      } finally {
        mo.observe(doc.body, { childList: true, subtree: true });
      }
    });
    mo.observe(doc.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ *
   * 快捷示例（自写 JS 的工具用；走 ToolRunner 的工具由运行器内部处理）
   * ------------------------------------------------------------------ */

  /** 渲染「试试 + 示例按钮」，点选后交给调用方回填并重算。
   *  容器须为 <div class="tool-examples" data-examples[="mode"]></div>，折叠由 examplesCollapse 接管
   *  （按钮类名必须 .tool-example，否则折叠与全站点击器都认不出）。
   *  sel：可选容器选择器，缺省取 root 内第一个 [data-examples]；多面板工具
   *  （date-calculator / encoding-converter / timestamp-converter）按模式拆分
   *  示例区时传 '[data-examples="<mode>"]'，与 ratio/power 的每面板容器同构。
   *  返回是否渲染了示例：无容器 / 无示例 / 未传回调时返回 false，调用方无需判断。 */
  function examples(root, list, onPick, sel) {
    var box = root && root.querySelector ? root.querySelector(sel || '[data-examples]') : null;
    if (!box || !list || !list.length || typeof onPick !== 'function') { return false; }
    box.innerHTML = '<span class="tool-examples-label"><svg class="tool-icon" aria-hidden="true"><use href="#i-zap"></use></svg>' + escapeHtml(t('ui.example', '试试')) + '</span>' +
      list.map(function (ex, i) {
        return '<button class="tool-example" type="button" data-ex="' + i + '">' +
          escapeHtml(ex.label == null ? '' : ex.label) + '</button>';
      }).join('');
    box.addEventListener('click', function (ev) {
      var btn = ev.target && ev.target.closest ? ev.target.closest('.tool-example') : null;
      if (!btn) { return; }
      var i = parseInt(btn.getAttribute('data-ex'), 10);
      var ex = list[i];
      if (!ex) { return; }
      onPick(ex, i);
    });
    return true;
  }

  /* ------------------------------------------------------------------ *
   * 自动初始化
   * ------------------------------------------------------------------ */
  ready(function () {
    // 文案填充放最前：HTML 侧默认值/可访问性名称要尽早到位，也不受后续 init 异常牵连
    fillDeclaredCopy();
    theme.init();
    backTop();
    homeFilter();
    datePicker();
    examplesCollapse();
    enhanceA11y();

    // 声明式复制按钮：<button data-tool-copy="#targetId">
    qa('[data-tool-copy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var src = q(btn.getAttribute('data-tool-copy'));
        copy(src ? (src.getAttribute('data-copy-value') || src.textContent) : '', btn);
      });
    });

    // 我的收藏 + 最近使用：工具页同步按钮两态并记录到访，首页渲染两行聚合
    favs();
    visits();
    recordVisit(pageSlug());
  });

  /* ------------------------------------------------------------------ *
   * 组件：轻量 Markdown 渲染（AI 回答用）
   * ------------------------------------------------------------------ */
  function mdEl(tag, cls) {
    var node = doc.createElement(tag);
    if (cls) { node.className = cls; }
    return node;
  }

  /**
   * 行内格式：**加粗** 与 `行内代码`；其余一律 textContent（不做任何 HTML 解析）。
   * optimistic=true（流式中间态）：行尾**尚未闭合**的 ** 或 ` 也直接按最终形态渲染——
   * 否则闭合计号到达的一瞬间字重/底色突变，整段「抖一下」（2026-09-15 用户实测）。
   * 闭合后乐观与严格两种口径渲染结果一致，故收尾零跳变；终态一律走严格口径
   * （正确性优先：模型真输出了不成对的字面 ** 时，收尾把星号如显出来，罕见且只小跳一次）。
   */
  function mdInline(host, raw, optimistic) {
    var re = optimistic
      ? /(\*\*[^*\n]+\*\*|\*{2}[^*\n]*$|`[^`\n]+`|`[^`\n]*$)/g
      : /(\*\*[^*\n]+\*\*|`[^`\n]+`)/g;
    var parts = String(raw == null ? '' : raw).split(re);
    for (var i = 0; i < parts.length; i++) {
      var s = parts[i];
      if (!s) { continue; }
      if (s.length > 4 && s.slice(0, 2) === '**' && s.slice(-2) === '**') {
        var b = mdEl('strong', '');
        b.textContent = s.slice(2, -2);
        host.appendChild(b);
      } else if (optimistic && s.length >= 2 && s.slice(0, 2) === '**') {
        var ob = mdEl('strong', '');
        ob.textContent = s.slice(2);
        host.appendChild(ob);
      } else if (s.length > 2 && s.charAt(0) === '`' && s.charAt(s.length - 1) === '`') {
        var c = mdEl('code', 'tool-md-code');
        c.textContent = s.slice(1, -1);
        host.appendChild(c);
      } else if (optimistic && s.charAt(0) === '`') {
        var oc = mdEl('code', 'tool-md-code');
        oc.textContent = s.slice(1);
        host.appendChild(oc);
      } else {
        host.appendChild(doc.createTextNode(s));
      }
    }
  }

  function mdLine(tag, cls, raw, optimistic) {
    var node = mdEl(tag, cls);
    mdInline(node, raw, optimistic);
    return node;
  }

  /**
   * 把 Markdown 子集渲染进容器（AI 回答专用）。
   *
   * 为什么不引 marked / markdown-it：
   *   ① 本站卖点是断网可用、零外部依赖，外链 CDN 一律不用；
   *   ② AI 回答是**不可信输入**，传统 md→HTML 做法是「拼字符串再 innerHTML」，
   *      等于把 XSS 口子交给模型。这里全程 createElement + textContent，**没有任何 innerHTML**：
   *      模型就算输出 <img src=x onerror=alert(1)>，也只会作为普通文字显示出来。
   *
   * 支持的子集（刻意收窄，且与后端提示词里允许的语法一致）：
   *   # ~ #### 标题 · -/* 无序列表 · 1. 有序列表 · **加粗** · `行内代码` · --- 分隔线 · 空行分段
   * 不支持也不猜：表格、代码块、图片、链接、嵌套列表、内联 HTML（一律按纯文本显示）。
   */
  function md(host, text, opts) {
    if (!host) { return; }
    host.textContent = '';
    // optimistic：流式中间态乐观渲染。行级口径：
    //   hr：2 个以上 -/*/_ 即按分隔线（严格口径要求 3 个）——分片常切在 --- 中间；
    //   标题：行首 #~#### 后跟空格或行未完即按标题（级别=井号数，空格到达后锁定不再变）；
    //   列表**不做乐观**：行首裸 - 在 AI 回答里常见于负数/区间（如「-20%」），乐观误判会引入新跳变。
    var op = !!(opts && opts.optimistic);
    var lines = String(text == null ? '' : text).replace(/\r\n?/g, '\n').split('\n');
    var list = null;
    var para = [];

    function flushPara() {
      if (!para.length) { return; }
      // 段落内保留原换行（容器 CSS 是 pre-wrap），与改造前的整体行为一致
      host.appendChild(mdLine('p', 'tool-md-p', para.join('\n'), op));
      para = [];
    }

    for (var i = 0; i < lines.length; i++) {
      var raw = lines[i];
      var t = raw.replace(/^\s+|\s+$/g, '');
      if (t === '') { flushPara(); list = null; continue; }
      if (/^(-{3,}|\*{3,}|_{3,})$/.test(t) || (op && /^(-{2,}|\*{2,}|_{2,})$/.test(t))) {
        flushPara();
        list = null;
        host.appendChild(mdEl('hr', 'tool-md-hr'));
        continue;
      }
      var h = /^(#{1,4})\s+(.+)$/.exec(t) || (op ? /^(#{1,4})(?:\s+(.*))?$/.exec(t) : null);
      if (h) {
        flushPara();
        list = null;
        host.appendChild(mdLine('p', 'tool-md-h tool-md-h' + h[1].length, h[2], op));
        continue;
      }
      var ul = /^[-*+]\s+(.+)$/.exec(t);
      var ol = /^\d+[.)]\s+(.+)$/.exec(t);
      if (ul || ol) {
        flushPara();
        var want = ul ? 'ul' : 'ol';
        if (!list || String(list.tagName).toLowerCase() !== want) {
          list = mdEl(want, 'tool-md-list');
          host.appendChild(list);
        }
        list.appendChild(mdLine('li', '', (ul || ol)[1], op));
        continue;
      }
      list = null;
      para.push(raw);
    }
    flushPara();
  }

  /* ------------------------------------------------------------------ *
   * 输入框内联校验（blur 绿/红框 + 状态行，yaml-json-converter 同款交互的共享版）
   *   check(value, reportEmpty) -> { ok: bool, msg: string } | null
   *   返回 null = 不表态（如空输入不打扰），此时清掉旧状态
   * ------------------------------------------------------------------ */
  function fieldValidator(opts) {
    var input = opts.input;
    var state = opts.state;
    var check = opts.check;
    function setState(ok, msg) {
      if (!input) { return; }
      input.className = input.className.replace(/\s*is-(?:valid|invalid)/g, '') +
        (ok ? ' is-valid' : ' is-invalid');
      if (state) {
        state.textContent = msg || '';
        state.className = 'tool-fstate ' + (ok ? 'tool-fstate--ok' : 'tool-fstate--err');
        state.hidden = !msg;
        if (msg && pulse) { pulse(state); }
      }
    }
    function clear() {
      if (!input) { return; }
      input.className = input.className.replace(/\s*is-(?:valid|invalid)/g, '');
      if (state) {
        state.hidden = true;
        state.textContent = '';
        state.className = 'tool-fstate';
      }
    }
    function run(reportEmpty) {
      var value = input && input.value != null ? String(input.value) : '';
      var r = check(value, reportEmpty !== false);
      if (!r) { clear(); return; }
      setState(r.ok, r.msg);
    }
    return { setState: setState, clear: clear, run: run };
  }

  /* ------------------------------------------------------------------ *
   * 组件：文件拖拽上传区（dropzone）
   * 结构：<div class="tool-dropzone" data-dropzone tabindex="0" role="button">
   *         <svg class="tool-icon">…</svg>
   *         <span class="tool-dropzone-text" data-dropzone-text></span>
   *         <input class="tool-file-hidden" type="file" …>
   *       </div>
   * 行为：点击 / Enter·Space 触发原生文件框；拖拽高亮，松手把 files 写回
   *       input.files（DataTransfer）并派发 change —— 工具侧既有 change 监听零改动。
   * 接线：UI.dropzone(zoneEl, inputEl)；可选 UI.dropzone(zone, input, { pickedText })。
   * ------------------------------------------------------------------ */
  function dropzone(zone, input, opts) {
    if (!zone || !input || zone.getAttribute('data-dropzone-ready') === '1') { return; }
    zone.setAttribute('data-dropzone-ready', '1');

    var lang = (global.ToolI18n || {})[global.TOOLS_LANG || 'zh-CN'] || {};
    var msgs = (lang.ui || {}).dropzone || {};
    var textEl = zone.querySelector('[data-dropzone-text]');
    var defaultText = (opts && opts.pickedText) || msgs.choose || '点击选择或拖拽文件到这里';

    function setText(s) {
      if (textEl) { textEl.textContent = s; }
    }
    setText(defaultText);

    function refresh() {
      var files = input.files;
      if (files && files.length === 1) {
        setText((msgs.picked || '已选：{name}').replace('{name}', files[0].name));
        zone.className = zone.className.replace(/\s*is-picked/, '') + ' is-picked';
      } else if (files && files.length > 1) {
        setText((msgs.pickedMany || '已选 {n} 个文件').replace('{n}', String(files.length)));
        zone.className = zone.className.replace(/\s*is-picked/, '') + ' is-picked';
      } else {
        setText(defaultText);
        zone.className = zone.className.replace(/\s*is-picked/, '');
      }
    }
    input.addEventListener('change', refresh);

    zone.addEventListener('click', function (ev) {
      if (ev.target === input) { return; }   // 防合成 click 冒泡双触发
      input.click();
    });
    zone.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        input.click();
      }
    });

    function hasFiles(ev) {
      var dt = ev.dataTransfer;
      return dt && dt.types && Array.prototype.indexOf.call(dt.types, 'Files') >= 0;
    }
    zone.addEventListener('dragenter', function (ev) { ev.preventDefault(); if (hasFiles(ev)) { zone.className = zone.className.replace(/\s*is-drag/, '') + ' is-drag'; } });
    zone.addEventListener('dragover', function (ev) { ev.preventDefault(); if (hasFiles(ev)) { zone.className = zone.className.replace(/\s*is-drag/, '') + ' is-drag'; } });
    zone.addEventListener('dragleave', function () { zone.className = zone.className.replace(/\s*is-drag/, ''); });
    zone.addEventListener('drop', function (ev) {
      ev.preventDefault();
      zone.className = zone.className.replace(/\s*is-drag/, '');
      var files = ev.dataTransfer && ev.dataTransfer.files;
      if (!files || !files.length) { return; }
      try {
        var dt = new DataTransfer();
        for (var i = 0; i < files.length; i++) { dt.items.add(files[i]); }
        input.files = dt.files;
      } catch (e) { /* 极老环境：放弃写回，change 后侧通道读不到会给出明确报错 */ }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------------ *
   * 组件：文件预览区（preview）
   * 结构：<div class="tool-preview" id="xx-prev" hidden></div>
   * 行为：show(items, opts) 渲染缩略图网格；demo 来源附引导条
   *       （demoNote + ui.preview.guide），上传来源附「移除文件」条
   *       （点击回调 opts.onRemove，由工具侧清空字节通道回 demo）。
   *       构造时传入 input 则自动监听 change：选中文件即刻出图——
   *       数据源由工具侧按字节通道隐式判定（demo/upload），无 source 下拉。
   * 幂等：同状态零 DOM 写入（签名比对）；objectURL 不可用时字节走
   *       dataURL 回退（jsdom），File 无 objectURL 时只出图注不出图。
   * 接线：var prev = UI.preview(el, input, { demoNote, onRemove });
   *       calc 里按「有无上传字节」分流：demo 分支 prev.show(items,{demo:true})。
   * ------------------------------------------------------------------ */
  function preview(container, input, opts) {
    if (!container || container.getAttribute('data-preview-ready') === '1') { return null; }
    container.setAttribute('data-preview-ready', '1');

    var lang = (global.ToolI18n || {})[global.TOOLS_LANG || 'zh-CN'] || {};
    var msgs = (lang.ui || {}).preview || {};
    var demoNote = (opts && opts.demoNote) || '';
    var guide = msgs.guide || '点上传区换成你自己的文件';
    var removeText = msgs.remove || '移除文件，恢复示例图';
    var onRemove = (opts && opts.onRemove) || null;
    var hasOURL = false;
    try {
      hasOURL = typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' && typeof URL.revokeObjectURL === 'function';
    } catch (e) { hasOURL = false; }

    var urls = [];
    var token = 0;
    var lastSig = null;

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function bytesToUrl(bytes, mime) {
      if (hasOURL) { return URL.createObjectURL(new Blob([bytes], { type: mime || 'image/png' })); }
      var bin = '';
      var CH = 0x8000;
      for (var i = 0; i < bytes.length; i += CH) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + CH, bytes.length)));
      }
      return 'data:' + (mime || 'image/png') + ';base64,' + btoa(bin);
    }
    function releaseUrls() {
      urls.forEach(function (u) {
        if (u && u.indexOf('data:') !== 0) { try { URL.revokeObjectURL(u); } catch (e) { /* 忽略 */ } }
      });
      urls = [];
    }
    function itemUrl(it) {
      try {
        if (it.file) { return hasOURL ? URL.createObjectURL(it.file) : ''; }
        return bytesToUrl(it.bytes, it.mime);
      } catch (e) { return ''; }
    }
    function sig(items, isDemo, tk) {
      return (isDemo ? 'd' : 'u') + '|' + tk + '|' + (items || []).map(function (it) {
        return (it.name || '') + ':' + (it.mime || '') + ':' + (it.bytes ? it.bytes.length : 0) + (it.file ? ':' + (it.file.size || 0) : '');
      }).join(',');
    }
    function render(items, isDemo) {
      releaseUrls();
      var html = '';
      if (isDemo) {
        if (demoNote || guide) {
          html += '<p class="tool-preview-note">' + esc([demoNote, guide].filter(Boolean).join('，')) + '</p>';
        }
      } else if (onRemove) {
        html += '<p class="tool-preview-bar"><button type="button" class="tool-preview-remove">' + esc(removeText) + '</button></p>';
      }
      /* 多图预览在舞台里回缩略格网格：合并/转 PDF 一次多张，单列大图会把页面撑爆 */
      html += '<div class="tool-preview-grid' + ((items || []).length > 1 ? ' tool-preview-grid--multi' : '') + '">' + (items || []).map(function (it) {
        var url = itemUrl(it);
        if (url && url.indexOf('data:') !== 0) { urls.push(url); }
        return '<figure class="tool-preview-item">' +
          (url ? '<img src="' + url + '" alt="' + esc(it.name || '图片预览') + '" decoding="async">' : '') +
          '<figcaption>' + esc(it.name || '') + '</figcaption>' +
          '</figure>';
      }).join('') + '</div>';
      container.innerHTML = html;
      container.hidden = false;
    }

    function show(items, showOpts) {
      if (!items || !items.length) { clear(); return; }
      var isDemo = !!(showOpts && showOpts.demo);
      var tk = isDemo ? 0 : ((showOpts && showOpts.token) || 0);
      var s = sig(items, isDemo, tk);
      if (s === lastSig) { return; }   /* 同状态零写入 */
      lastSig = s;
      render(items, isDemo);
    }
    function clear() {
      if (lastSig === null) { return; }
      lastSig = null;
      releaseUrls();
      container.innerHTML = '';
      container.hidden = true;
    }
    function showFiles(files) {
      var list = (files || []).map(function (f) {
        return { file: f, name: f.name, mime: f.type || 'image/png' };
      });
      show(list, { token: ++token });
    }

    if (input) {
      input.addEventListener('change', function () {
        var files = (input.files && input.files.length) ? Array.prototype.slice.call(input.files) : [];
        if (!files.length) { return; }   /* 清空路径交给工具侧 calc（重置/移除后走 demo 分支） */
        showFiles(files);
      });
    }
    container.addEventListener('click', function (ev) {
      var btn = ev.target && ev.target.closest ? ev.target.closest('.tool-preview-remove') : null;
      if (btn && onRemove) { onRemove(); }
    });

    return { show: show, showFiles: showFiles, clear: clear };
  }

  /* ------------------------------------------------------------------ *
   * 导出
   * ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ *
   * 组件二十二：动态行项目表（rowTable）—— PI/CI/PL 等单据类的行编辑
   *
   * 结构：容器内渲染 table（thead 来自 cols 的 label，零内置文案） +
   *       tbody 行（每格 input.tool-input[data-rt=key]，末格删除按钮） +
   *       「添加一行」按钮（文案 opts.addText，由语言包传入）。
   * 行为：rows() 读全部行（按 cols.key 组对象）；setRows(data) 整表重建；
   *       增删行保持 [opts.minRows, opts.maxRows]；任何输入 / 增删都会冒泡
   *       input/click 到容器 —— runner.mount 的 config.watch 委托它触发重算，
   *       组件自身不感知 runner。
   * a11y：删除按钮 aria-label 用 opts.removeText；表格原生语义。
   * 幂等：data-rowtable-ready 守卫；setRows 用签名比对避免同数据重建。
   * ------------------------------------------------------------------ */
  function rowTable(host, cols, opts) {
    if (!host || !cols || !cols.length || host.getAttribute('data-rowtable-ready') === '1') { return null; }
    host.setAttribute('data-rowtable-ready', '1');
    host.className += ' tool-rowtable';
    var o = opts || {};
    var minRows = o.minRows == null ? 1 : o.minRows;
    var maxRows = o.maxRows == null ? 60 : o.maxRows;
    var addText = o.addText || '+';
    var removeText = o.removeText || '×';
    var esc = escapeHtml;
    var attrEsc = function (s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    };

    var table = doc.createElement('table');
    table.className = 'tool-rowtable-table';
    if (o.ariaLabel) { table.setAttribute('aria-label', o.ariaLabel); }
    table.innerHTML = '<thead><tr>' + cols.map(function (c) {
      return '<th' + (c.align === 'right' ? ' class="is-num"' : '') + '>' + esc(c.label) + '</th>';
    }).join('') + '<th class="tool-rowtable-op" aria-hidden="true"></th></tr></thead><tbody></tbody>';
    var tbody = table.querySelector('tbody');
    var scroll = doc.createElement('div');
    scroll.className = 'tool-rowtable-scroll';
    scroll.appendChild(table);
    host.appendChild(scroll);

    var addBtn = doc.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'tool-btn tool-btn--sm tool-btn--text tool-rowtable-add';
    addBtn.setAttribute('data-rt-add', '1');
    addBtn.textContent = addText;
    host.appendChild(addBtn);

    function rowEls() {
      return Array.prototype.slice.call(tbody.querySelectorAll('tr'));
    }
    function buildRow(vals) {
      var tr = doc.createElement('tr');
      tr.innerHTML = cols.map(function (c, i) {
        /* 下拉列（type:'select'）：有限集取值，option 文本走 esc 防注入；
           rows() 读 .value 对 select 同样成立，收集逻辑零改动 */
        if (c.type === 'select') {
          var cur = vals && vals[i] != null ? String(vals[i]) : '';
          var optsHtml = (c.options || []).map(function (op) {
            var v = op && op.value != null ? String(op.value) : '';
            var t = op && op.label != null ? op.label : v;
            return '<option value="' + attrEsc(v) + '"' + (v === cur ? ' selected' : '') + '>' + esc(t) + '</option>';
          }).join('');
          return '<td><select class="tool-input tool-rowtable-input" data-rt="' + esc(c.key) + '"' +
            ' aria-label="' + attrEsc(c.label) + '">' + optsHtml + '</select></td>';
        }
        var num = c.type === 'num';
        return '<td><input class="tool-input tool-rowtable-input" type="text"' +
          (num ? ' inputmode="decimal"' : '') + ' data-rt="' + esc(c.key) + '"' +
          (c.align === 'right' ? ' style="text-align:right"' : '') +
          ' aria-label="' + attrEsc(c.label) + '"' +
          ' value="' + attrEsc(vals && vals[i] != null ? vals[i] : '') + '" autocomplete="off"></td>';
      }).join('') +
        '<td class="tool-rowtable-op"><button class="tool-btn tool-btn--sm tool-btn--text" type="button" data-rt-del="1" aria-label="' +
        attrEsc(removeText) + '">&times;</button></td>';
      return tr;
    }
    function syncAddBtn() {
      var over = rowEls().length >= maxRows;
      addBtn.disabled = over;
      if (over) { addBtn.setAttribute('aria-disabled', 'true'); }
      else { addBtn.removeAttribute('aria-disabled'); }
    }
    function setRows(data) {
      /* 空数组的语义随 minRows 走：minRows ≥1 时兜底 1 空行（原行为，
       * PI/CI/PL 等单据消费者不变）；minRows 0（如会议表行动项「没有行动项」）
       * 显式空数组 = 真空表，0 行合法。 */
      var list = (data && data.length ? data : (minRows ? [[]] : [])).slice(0, maxRows);
      while (list.length < minRows) { list.push([]); }
      tbody.innerHTML = '';
      list.forEach(function (vals) { tbody.appendChild(buildRow(vals)); });
      syncAddBtn();
    }
    function rows() {
      return rowEls().map(function (tr) {
        var o2 = {};
        cols.forEach(function (c) {
          var inp = tr.querySelector('[data-rt="' + c.key + '"]');
          o2[c.key] = inp ? inp.value : '';
        });
        return o2;
      });
    }
    function addRow(vals) {
      if (rowEls().length >= maxRows) { return; }
      tbody.appendChild(buildRow(vals || []));
      syncAddBtn();
    }
    host.addEventListener('click', function (ev) {
      var del = ev.target && ev.target.closest ? ev.target.closest('[data-rt-del]') : null;
      if (del) {
        var tr = del.closest('tr');
        if (tr && rowEls().length > minRows) { tr.parentNode.removeChild(tr); }
        return;
      }
      if (ev.target && ev.target.closest && ev.target.closest('[data-rt-add]')) { addRow([]); }
    });
    setRows(o.initial);
    return { rows: rows, setRows: setRows, addRow: addRow, el: host, tableEl: table };
  }

  /* ------------------------------------------------------------------ *
   * 组件二十三：A4 单据纸面（paper）—— PI/CI/PL 预览与打印隔离
   *
   * 结构：容器内渲染 .tool-paper（白底 A4 比例纸面，**固定纸面色**：
   *       单据是打印物，暗色模式下保持白纸黑字，打印才是所见即所得）。
   * render(blocks)：按引擎 invoiceDoc 的七型 blocks 渲染 DOM；签名比对，
   *       同输入零写入（快照不抖）。空值行引擎侧已剔除，这里只认类型。
   * print()：body[data-print-doc] 标记 + window.print()，CSS 用 visibility
   *       穿透方案只印纸面（无需枚举站头站尾类名，见 tools.css @media print）。
   * a11y：容器 role="region" + aria-label（opts.ariaLabel）。
   * ------------------------------------------------------------------ */
  function paper(host, opts) {
    if (!host || host.getAttribute('data-paper-ready') === '1') { return null; }
    host.setAttribute('data-paper-ready', '1');
    var o = opts || {};
    host.className += ' tool-paper-wrap';
    if (!host.hasAttribute('hidden')) { host.setAttribute('hidden', ''); }
    if (o.ariaLabel) { host.setAttribute('aria-label', o.ariaLabel); host.setAttribute('role', 'region'); }

    var esc = function (s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    };
    var sheet = null;
    var lastSig = null;

    function el(tag, cls, text) {
      var n = doc.createElement(tag);
      if (cls) { n.className = cls; }
      if (text != null) { n.textContent = text; }
      return n;
    }
    function buildBlock(b) {
      switch (b.type) {
        case 'title': return el('div', 'tp-title', b.text);
        case 'kv':
          var kv = el('div', 'tp-kv');
          (b.rows || []).forEach(function (r) {
            kv.appendChild(el('span', 'tp-k', r[0]));
            kv.appendChild(el('span', 'tp-v', r[1]));
          });
          return kv;
        case 'party':
          var p = el('div', 'tp-party');
          [['tp-party-l', b.leftTitle, b.leftLines], ['tp-party-r', b.rightTitle, b.rightLines]].forEach(function (side) {
            var box = el('div', side[0]);
            if (side[1]) { box.appendChild(el('div', 'tp-party-t', side[1])); }
            (side[2] || []).forEach(function (ln) { box.appendChild(el('div', 'tp-party-line', ln)); });
            p.appendChild(box);
          });
          return p;
        case 'table':
          var wrap = el('div', 'tp-table-wrap');
          var tb = el('table', 'tp-table' + (b.dense ? ' tp-table--dense' : ''));
          var align = b.align || [];
          tb.innerHTML = '<thead><tr>' + (b.head || []).map(function (h, i) {
            return '<th' + (align[i] === 'right' ? ' class="is-num"' : '') + '>' + esc(h) + '</th>';
          }).join('') + '</tr></thead><tbody>' +
            (b.rows || []).map(function (r) {
              return '<tr>' + r.map(function (c, i) {
                return '<td' + (align[i] === 'right' ? ' class="is-num"' : '') + '>' + esc(c) + '</td>';
              }).join('') + '</tr>';
            }).join('') + '</tbody>';
          wrap.appendChild(tb);
          wrap.setAttribute('tabindex', '0');
          return wrap;
        case 'totals':
          var ts = el('div', 'tp-totals');
          (b.rows || []).forEach(function (r, i) {
            var hl = (b.highlight || []).indexOf(i) >= 0;
            ts.appendChild(el('span', 'tp-tot-k' + (hl ? ' is-total' : ''), r[0]));
            ts.appendChild(el('span', 'tp-tot-v' + (hl ? ' is-total' : ''), r[1]));
          });
          return ts;
        case 'note':
          var nt = el('div', 'tp-note');
          String(b.text == null ? '' : b.text).split('\n').forEach(function (ln, i) {
            if (i) { nt.appendChild(el('div', 'tp-note-gap')); }
            nt.appendChild(el('div', 'tp-note-line', ln));
          });
          return nt;
        case 'sign':
          var sg = el('div', 'tp-sign');
          [b.left, b.right].forEach(function (side) {
            var box = el('div', 'tp-sign-col');
            box.appendChild(el('div', 'tp-sign-line'));
            if (side) { box.appendChild(el('div', 'tp-sign-name', side)); }
            sg.appendChild(box);
          });
          return sg;
        default: return null;
      }
    }
    function render(blocks) {
      if (!blocks || !blocks.length) { return false; }
      var sig = JSON.stringify(blocks);
      if (sig === lastSig && sheet) { return false; } // 同状态零写入
      lastSig = sig;
      if (sheet) { host.removeChild(sheet); }
      sheet = el('div', 'tool-paper');
      blocks.forEach(function (b) {
        var node = buildBlock(b);
        if (node) { sheet.appendChild(node); }
      });
      host.appendChild(sheet);
      host.removeAttribute('hidden');
      return true;
    }
    function clear() {
      if (sheet) { host.removeChild(sheet); sheet = null; }
      lastSig = null;
      host.setAttribute('hidden', '');
    }
    function print(opts) {
      if (!sheet) { return; }
      /* opts.title：打印期间置换 document.title（浏览器页眉显示它，默认是页面 SEO 标题，
         单据场景换成单据号更有意义）；afterprint 或兜底复位 */
      var newTitle = opts && opts.title ? String(opts.title) : '';
      var prevTitle = newTitle ? doc.title : '';
      var restored = false;
      if (newTitle) { doc.title = newTitle; }
      doc.body.setAttribute('data-print-doc', '1');
      /* 纸面祖先链标记：CSS 无法从深层纸面反选祖先，打印分页修复
         （tools.css @media print 的 display 链规则）靠这条链圈定「保留布局」
         的范围——链外全部 display:none，文档高度=纸面自身高度。 */
      var chain = [];
      var p = host;
      while (p && p !== doc.body) { chain.push(p); p = p.parentElement; }
      if (p === doc.body) { chain.push(p); }
      chain.forEach(function (x) { x.className += ' tp-chain'; });
      var done = function () {
        if (restored) { return; }
        restored = true;
        doc.body.removeAttribute('data-print-doc');
        chain.forEach(function (x) {
          x.className = (' ' + x.className + ' ').replace(' tp-chain ', ' ').replace(/^\s+|\s+$/g, '');
        });
        if (newTitle) { doc.title = prevTitle; }
      };
      if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('afterprint', done, { once: true });
      }
      try { window.print(); } catch (e) { /* 忽略 */ }
      // afterprint 在部分环境不触发（如取消打印）：延时兜底复位
      setTimeout(done, 2000);
    }
    return { render: render, clear: clear, print: print, el: host };
  }

  /* ------------------------------------------------------------------ *
   * 组件二十一：结果可分享（把当前输入编码进 URL，打开即回填重算）
   *
   * 为什么用 hash 而不是 query：hash 不发送到服务器 —— 不影响页面缓存、不影响 canonical，
   * 也不必为「带参数的重复页面」加额外的 robots 规则（query 形态会派生一批参数变体 URL）。
   *
   * 全站零工具改动：自动挂到每个 .tool-result 上，序列化「所属面板内」的表单值。
   * 局限：把状态放在 canvas 上的工具（手写签名 / 在线尺子 / 绘图）没有表单值可序列化，
   * 按钮对这些工具退化为「分享当前页面」，属可接受降级。
   * ------------------------------------------------------------------ */
  var SHARE_HASH = '#r=';

  /** 是否为可参与分享的表单控件（排除文件、密码、隐藏、搜索与首页搜索框） */
  function shareField(el) {
    var tag = (el.tagName || '').toLowerCase();
    if (tag !== 'input' && tag !== 'select' && tag !== 'textarea') return false;
    var type = (el.type || '').toLowerCase();
    if (type === 'file' || type === 'password' || type === 'hidden' || type === 'search') return false;
    return !el.hasAttribute('data-tool-search');
  }

  /** 把 scope 内的表单值编码成 a=1&b=2（上限 12 项，防超长 URL） */
  function shareEncode(scope) {
    if (!scope || !scope.querySelectorAll) return '';
    var els = scope.querySelectorAll('input, select, textarea');
    var out = [];
    for (var i = 0; i < els.length && out.length < 12; i++) {
      var el = els[i];
      if (!el.id || !shareField(el)) continue;
      var type = (el.type || '').toLowerCase();
      var box = (type === 'checkbox' || type === 'radio');
      if (box && !el.checked) continue;
      var val = box ? '1' : String(el.value == null ? '' : el.value);
      // 空值不写进链接：URL 更短，回填时也不会覆盖该控件的默认值
      if (val === '') continue;
      out.push(encodeURIComponent(el.id) + '=' + encodeURIComponent(val));
    }
    return out.join('&');
  }

  /** 复制文本：Clipboard API 不可用时退回 execCommand（与 ToolUI.copy 同口径） */
  function shareCopyText(text, done) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function () { done(true); }, function () { done(false); });
      return;
    }
    var ta = doc.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    doc.body.appendChild(ta);
    ta.select();
    var ok = false;
    try { ok = doc.execCommand('copy'); } catch (e) { ok = false; }
    doc.body.removeChild(ta);
    done(ok);
  }

  /** 注入分享按钮（幂等：已存在即跳过，同状态零 DOM 写入） */
  function sharePaint() {
    if (!doc || !doc.querySelectorAll) return;
    var bodies = doc.querySelectorAll('.tool-result-body');
    for (var i = 0; i < bodies.length; i++) {
      var body = bodies[i];
      if (body.querySelector('[data-tool-share]')) continue;
      var btn = doc.createElement('button');
      btn.type = 'button';
      // 类名的两个作用：① tool-btn-copy 复用结果区按钮的尺寸与交互；
      // ② tool-result-share 做视觉区分 —— 早先与「复制」按钮同款同图标，用户看不出这是分享入口。
      btn.className = 'tool-btn-copy tool-result-share';
      btn.setAttribute('data-tool-share', '');
      btn.setAttribute('aria-label', t('ui.share.aria', '复制这个结果的链接'));
      btn.innerHTML = '<svg class="tool-icon" aria-hidden="true"><use href="#i-share"></use></svg>'
        + '<span data-share-text>' + t('ui.share.btn', '分享链接') + '</span>';
      body.appendChild(btn);
    }
  }

  function shareClick(ev) {
    var btn = ev.target && ev.target.closest ? ev.target.closest('[data-tool-share]') : null;
    if (!btn) return;
    // 面板 > 工具卡 > 整页：多面板工具各面板独立分享，互不串值
    var panel = (btn.closest && (btn.closest('.tool-panel') || btn.closest('.tool-card--tool'))) || doc;
    var encoded = shareEncode(panel);
    var url = location.origin + location.pathname + (encoded ? SHARE_HASH + encoded : '');

    // 让地址栏与复制内容一致（replaceState 不产生历史条目、不刷新页面）
    try { history.replaceState(null, '', url); } catch (e) { /* 个别环境禁用 history：忽略 */ }

    if (encoded && navigator.share) {
      navigator.share({ title: doc.title, url: url }).catch(function () { /* 用户取消：静默 */ });
      return;
    }
    shareCopyText(url, function (ok) {
      toast(ok ? t('ui.share.copied', '链接已复制') : t('ui.share.failed', '复制失败，请手动复制地址栏链接'), ok ? 'ok' : 'warn', 1800);
    });
  }

  /** 打开带 #r= 的链接时回填并触发重算（同值零写入） */
  function shareRestore() {
    var h = location.hash || '';
    if (h.indexOf(SHARE_HASH) !== 0) return;
    var pairs = h.slice(SHARE_HASH.length).split('&');
    var touched = [];
    for (var i = 0; i < pairs.length; i++) {
      var eq = pairs[i].indexOf('=');
      if (eq < 1) continue;
      var id = '';
      try { id = decodeURIComponent(pairs[i].slice(0, eq)); } catch (e) { continue; }
      var el = q('#' + id);
      if (!el || !shareField(el)) continue;
      var val = '';
      try { val = decodeURIComponent(pairs[i].slice(eq + 1)); } catch (e) { continue; }
      var type = (el.type || '').toLowerCase();
      if (type === 'checkbox' || type === 'radio') {
        var on = (val === '1');
        if (el.checked !== on) el.checked = on;
      } else if (el.value !== val) {
        el.value = val;
      }
      touched.push(el);
    }
    // 触发工具重算：runner 与自管脚本都监听 input / change
    for (var j = 0; j < touched.length; j++) {
      touched[j].dispatchEvent(new Event('input', { bubbles: true }));
      touched[j].dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  var shareBound = false;

  var shareTick = 0;

  /** 一帧内合并多次补注入（工具重算可能连改几处 DOM） */
  function shareSchedule() {
    if (shareTick) return;
    var raf = global.requestAnimationFrame || function (fn) { return global.setTimeout(fn, 16); };
    shareTick = raf(function () { shareTick = 0; sharePaint(); });
  }

  /**
   * 用户主动操作后补注入（模式切换 / 重算可能重建结果区）。
   *
   * 这里刻意**不用常驻 MutationObserver 监听 body**：工具页每次输入都会重算并改动 DOM，
   * 观察器会让 sharePaint 的全文档 querySelectorAll 在每次按键时都跑一遍（纯浪费）。
   * 而结果区的重建总是由点击触发（tab / 计算 / 重置 / 分段控件），点击后补一次即可覆盖。
   * （本文件 1358 行记过同类教训：观察器 + 无条件写 DOM 曾自馈成死循环。）
   */
  function shareAfterAction(ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    if (!t.closest('.tool-tab, [data-calc], [data-reset], [data-segmented-btn], [data-mode]')) return;
    shareSchedule();
  }

  function shareAuto() {
    sharePaint();
    // 回填放在最后：此刻内联工具脚本均已执行完，触发 input 才会被工具接住
    shareRestore();
    if (shareBound) return;
    shareBound = true;
    doc.addEventListener('click', shareClick);
    doc.addEventListener('click', shareAfterAction);
  }

  var share = { paint: sharePaint, encode: shareEncode, restore: shareRestore };
  ready(shareAuto);

  global.ToolUI = {
    t: t,
    punct: punct,
    ready: ready,
    toast: toast,
    copy: copy,
    segmented: segmented,
    theme: theme,
    backTop: backTop,
    homeFilter: homeFilter,
    recent: recent,
    favs: favs,
    visits: visits,
    pulse: pulse,
    stepper: stepper,
    datePicker: datePicker,
    examples: examples,
    icon: icon,
    escapeHtml: escapeHtml,
    md: md,
    fieldValidator: fieldValidator,
    dropzone: dropzone,
    preview: preview,
    rowTable: rowTable,
    paper: paper,
    share: share
  };
})(typeof window !== 'undefined' ? window : globalThis);
